// [Codex 2026-07-28] Keep the global composer as the sole material layer.
import { ArrowUp, Mic, MicOff, Plus, Square } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEventHandler,
  type DragEventHandler,
  type KeyboardEvent,
  type TouchEventHandler,
} from "react";
import {
  AgentChatComposer,
  type AgentChatComposerHandle,
  type ComposerValue,
} from "../../premium/agent-chat-input/composer";
import { uiLibrarySourceAttrs } from "../../../lib/ui-library/ui-library-source-attrs";
import { uiLibraryThemeStyle } from "../../../lib/ui-library/ui-library-theme";
import { cn } from "../../../lib/utils";
import { ChatActivityIsland } from "./ChatActivityIsland";
import type {
  GlobalComposerController,
  GlobalComposerSlots,
} from "./composer-controller";

export interface GlobalChatComposerProps extends GlobalComposerSlots {
  controller: GlobalComposerController;
  composerRef?: React.RefObject<AgentChatComposerHandle | null>;
  placeholder?: string;
  compact?: boolean;
  attachOpen?: boolean;
  showAttach?: boolean;
  onAttachToggle?: () => void;
  voiceEnabled?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => boolean | void;
  onPaste?: ClipboardEventHandler<HTMLDivElement>;
  onDrop?: DragEventHandler<HTMLDivElement>;
  onDragOver?: DragEventHandler<HTMLDivElement>;
  onDragLeave?: DragEventHandler<HTMLDivElement>;
  onTouchStart?: TouchEventHandler<HTMLDivElement>;
  onTouchEnd?: TouchEventHandler<HTMLDivElement>;
  onFocusChange?: (focused: boolean) => void;
  className?: string;
}

export function GlobalChatComposer({
  controller,
  composerRef: externalRef,
  placeholder = "Message your analysts...",
  compact = false,
  attachOpen = false,
  showAttach = true,
  onAttachToggle,
  voiceEnabled,
  onKeyDown,
  onPaste,
  onDrop,
  onDragOver,
  onDragLeave,
  onTouchStart,
  onTouchEnd,
  onFocusChange,
  before,
  leadingActions,
  trailingActions,
  overlay,
  approval,
  className,
}: GlobalChatComposerProps) {
  const internalRef = useRef<AgentChatComposerHandle>(null);
  const composerRef = externalRef ?? internalRef;
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const change = (next: ComposerValue) => controller.onValueChange(next.text);
  const sendOrStop =
    controller.activity === "running" &&
    !controller.canSubmit &&
    controller.onStop
      ? controller.onStop
      : controller.onSubmit;

  return (
    <div
      {...uiLibrarySourceAttrs("beui-pro")}
      data-ui-library-surface
      data-global-chat-composer
      className={cn("relative", className)}
      style={uiLibraryThemeStyle()}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {overlay}
      {before}
      <ChatActivityIsland controller={controller} />
      <div className="fintheon-global-composer-shell relative overflow-visible rounded-[1.35rem] shadow-[inset_0_1px_0_color-mix(in_srgb,var(--fintheon-text)_8%,transparent)]">
        <AgentChatComposer
          ref={composerRef}
          value={controller.value}
          skills={[]}
          placeholder={placeholder}
          disabled={controller.disabled}
          minRows={1}
          maxRows={mobile ? 6 : 7}
          submitOnEnter={false}
          onChange={change}
          onEditorKeyDown={onKeyDown}
          onPaste={onPaste}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onFocus={() => onFocusChange?.(true)}
          onBlur={() => onFocusChange?.(false)}
          className={cn(
            "min-h-0 px-4 pb-2 pt-3 text-[13px] leading-6 text-[var(--fintheon-text)] md:text-sm",
            compact && "px-3",
          )}
        />
        {approval}
        <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 px-2 pb-2">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {showAttach ? (
              <button
                type="button"
                onClick={onAttachToggle}
                aria-expanded={attachOpen}
                aria-label={attachOpen ? "Close Attach" : "Open Attach"}
                className="inline-flex size-11 items-center justify-center bg-transparent text-[var(--fintheon-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fintheon-accent)]/45 md:size-8"
              >
                <Plus size={16} />
              </button>
            ) : null}
            {controller.onToggleVoice ? (
              <button
                type="button"
                onClick={controller.onToggleVoice}
                aria-pressed={voiceEnabled}
                aria-label={voiceEnabled ? "Turn voice off" : "Turn voice on"}
                className="inline-flex size-11 items-center justify-center bg-transparent text-[var(--fintheon-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fintheon-accent)]/45 md:size-8"
              >
                {voiceEnabled ? <MicOff size={15} /> : <Mic size={15} />}
              </button>
            ) : null}
            {leadingActions}
          </div>
          <div className="flex min-w-0 shrink-0 items-center gap-1">
            {trailingActions}
            <button
              type="button"
              onClick={sendOrStop}
              disabled={
                !controller.canSubmit && controller.activity !== "running"
              }
              aria-label={
                controller.activity === "running" && !controller.canSubmit
                  ? "Stop response"
                  : "Send message"
              }
              className="fintheon-send-button inline-flex size-11 items-center justify-center rounded-full md:size-[34px]"
            >
              {controller.activity === "running" && !controller.canSubmit ? (
                <Square size={12} fill="currentColor" />
              ) : (
                <ArrowUp size={16} strokeWidth={2.5} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
